import {Store} from 'react-notifications-component';
import {ACCESS_TOKEN, EMAIL, LANGUAGE, NAME} from './constants';
import {faEuro, faDollar, faPoundSign, faYen, faFrancSign, faBahtSign, faMoneyBill} from '@fortawesome/free-solid-svg-icons';
import {createIntl, createIntlCache} from 'react-intl';
import messages_fr from '../locales/fr.json';
import messages_en from '../locales/en.json';
import messages_es from '../locales/es.json';
import messages_it from '../locales/it.json';
import messages_de from '../locales/de.json';
import messages_pl from '../locales/pl.json';

const messages = {
  fr: messages_fr, en: messages_en, es: messages_es,
  it: messages_it, de: messages_de, pl: messages_pl
};
const intlCache = createIntlCache();
const getIntl = () => {
  const lang = localStorage.getItem(LANGUAGE) || (navigator.language || 'fr').split(/[-_]/)[0];
  const locale = messages[lang] ? lang : 'fr';
  return createIntl({locale, messages: messages[locale], defaultLocale: 'fr'}, intlCache);
};

const Utils = {
  logout: (history, sessionExpired) => {
    const wasLoggedIn = !!localStorage.getItem(ACCESS_TOKEN);
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(EMAIL);
    localStorage.removeItem(NAME);
    history.push('/');
    if (sessionExpired && wasLoggedIn) {
      const intl = getIntl();
      Utils.notifyWarning(
        intl.formatMessage({id: 'SESSION_EXPIRED_TITLE'}),
        intl.formatMessage({id: 'SESSION_EXPIRED_MESSAGE'})
      );
    }
  },
  isAccessToken: (history, noPush) => {
    if (!localStorage.getItem(ACCESS_TOKEN)) {
      if (!noPush) {
        history.push('/');
      }
      return false;
    }
    return true;
  },
  checkAccessTokenForLogin: (history) => {
    if (localStorage.getItem(ACCESS_TOKEN)) {
      history.push('/admin/dashboard');
    }
  },
  setBackground: (isColoredBackground) => {
    if (isColoredBackground === true) {
      // Fond « night » repris du site vitrine (bloc « Voyez le module en action ») : deux halos
      // radiaux (cyan en haut à droite, bleu en bas à gauche) sur un dégradé navy diagonal.
      // Ancré au viewport (fixed) pour garder les halos dans les coins au défilement.
      const night =
        'radial-gradient(1200px 600px at 78% -10%, rgba(52,205,236,.20), transparent 55%),' +
        'radial-gradient(900px 500px at 10% 110%, rgba(20,102,230,.28), transparent 55%),' +
        'linear-gradient(160deg, #0d1526, #131d34)';
      // Propriétés séparées : `background-attachment`/`repeat` à valeur unique se propagent aux 3
      // layers (sinon, dans le shorthand, `fixed` ne toucherait que le dernier). `background-color`
      // en repli au cas où les dégradés ne couvriraient pas (fallback + très petits écrans).
      document.body.setAttribute('style',
        `background-color: #0d1526 !important; background-image: ${night} !important; ` +
        'background-attachment: fixed !important; background-repeat: no-repeat !important');
    } else {
      document.body.setAttribute('style', 'background: #F5F8FB');
    }
  },
  notify: (title, message) => {
    Store.addNotification({
      title: title,
      message: message,
      type: 'default',
      insert: 'top',
      container: 'top-right',
      animationIn: ['animate__animated', 'animate__fadeIn'],
      animationOut: ['animate__animated', 'animate__fadeOut'],
      dismiss: {
        duration: 5000,
        onScreen: true
      }
    });
  },
  notifySuccess: (title, message) => {
    Store.addNotification({
      title: title,
      message: message,
      type: 'success',
      insert: 'top',
      container: 'top-right',
      animationIn: ['animate__animated', 'animate__fadeIn'],
      animationOut: ['animate__animated', 'animate__fadeOut'],
      dismiss: {
        duration: 5000,
        onScreen: true
      }
    });
  },
  notifyError: (title, message) => {
    Store.addNotification({
      title: title,
      message: message,
      type: 'danger',
      insert: 'top',
      container: 'top-right',
      animationIn: ['animate__animated', 'animate__fadeIn'],
      animationOut: ['animate__animated', 'animate__fadeOut'],
      dismiss: {
        duration: 5000,
        onScreen: true
      }
    });
  },
  notifyWarning: (title, message) => {
    Store.addNotification({
      title: title,
      message: message,
      type: 'warning',
      insert: 'top',
      container: 'top-right',
      animationIn: ['animate__animated', 'animate__fadeIn'],
      animationOut: ['animate__animated', 'animate__fadeOut'],
      dismiss: {
        duration: 5000,
        onScreen: true
      }
    });
  },
  getCurrencyIcon: (currency) => {
    switch (currency) {
      case 'EUR':
        return faEuro;
      case 'AUD':
      case 'CAD':
      case 'USD':
      case 'HKD':
      case 'NZD':
      case 'SGD':
      case 'MXN':
        return faDollar;
      case 'GBP':
      case 'GIP':
        return faPoundSign;
      case 'JPY':
        return faYen;
      case 'CHF':
        return faFrancSign;
      case 'THB':
        return faBahtSign;
      default:
        return faMoneyBill;
    }
  },
  getLanguage: () => {
    return localStorage.getItem(LANGUAGE) ? localStorage.getItem(LANGUAGE) : navigator.language.split(/[-_]/)[0]; // Language without region code
  },
  getSearchableSelectStyle: () => {
    return {

      control: (provided) => ({
        ...provided,
        borderRadius: '.5rem',
      }),

      multiValue: (provided) => ({
        ...provided,
        borderRadius: '.5rem'
      }),

      multiValueRemove: (provided) => ({
        ...provided,
        borderRadius: '.5rem'
      }),

      placeholder: (provided) => ({
        ...provided,
        borderRadius: '.5rem',
      }),

      singleValue: (provided, state) => {
        const opacity = state.isDisabled ? 0.5 : 1;
        const transition = 'opacity 300ms';

        return {
          ...provided,
          opacity,
          transition,
        };
      }
    };
  },
  isMobile() {
    return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 576;
  },
  // Numéro affiché d'une réservation. Les réservations post-MAJ ont un bookingNumber propre au
  // chauffeur, affiché "#N". Les anciennes (bookingNumber absent) retombent sur l'id brut, sans "#".
  // L'id reste la clé technique pour ouvrir/router une réservation — ne pas l'utiliser pour l'affichage.
  formatBookingNumber: (booking) => {
    if (!booking) return '';
    return booking.bookingNumber != null ? `#${booking.bookingNumber}` : `${booking.id}`;
  }
};

export default Utils;